import { Injectable, signal } from '@angular/core';
import { Notification } from '../../../domains/notification/models/notification.model';

export interface ToastNotification {
  id: string;
  notification: Notification;
  isExiting?: boolean;
}

@Injectable({ providedIn: 'root' })
export class NotificationToastService {
  private readonly _toasts = signal<ToastNotification[]>([]);
  readonly toasts = this._toasts.asReadonly();

  private readonly MAX_TOASTS = 3;
  private readonly AUTO_DISMISS_MS = 5000;
  private readonly timeouts = new Map<string, ReturnType<typeof setTimeout>>();

  show(notification: Notification): void {
    // Avoid duplicate active toasts with identical content
    const active = this._toasts();
    const existing = active.find(t => !t.isExiting && t.notification.body === notification.body);
    if (existing) {
      const existingTimeout = this.timeouts.get(existing.id);
      if (existingTimeout) {
        clearTimeout(existingTimeout);
      }
      const timeout = setTimeout(() => this.dismiss(existing.id), this.AUTO_DISMISS_MS);
      this.timeouts.set(existing.id, timeout);
      return;
    }

    const toast: ToastNotification = {
      id: `toast-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      notification
    };

    this._toasts.update(current => {
      const updated = [toast, ...current];
      return updated.slice(0, this.MAX_TOASTS);
    });

    const timeout = setTimeout(() => this.dismiss(toast.id), this.AUTO_DISMISS_MS);
    this.timeouts.set(toast.id, timeout);
  }

  dismiss(id: string): void {
    const existingTimeout = this.timeouts.get(id);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      this.timeouts.delete(id);
    }

    this._toasts.update(current =>
      current.map(t => t.id === id ? { ...t, isExiting: true } : t)
    );

    setTimeout(() => {
      this._toasts.update(current => current.filter(t => t.id !== id));
    }, 300);
  }

  dismissAll(): void {
    this.timeouts.forEach(timeout => clearTimeout(timeout));
    this.timeouts.clear();

    this._toasts.update(current =>
      current.map(t => ({ ...t, isExiting: true }))
    );

    setTimeout(() => {
      this._toasts.set([]);
    }, 300);
  }

  pauseAutoDismiss(id: string): void {
    const timeout = this.timeouts.get(id);
    if (timeout) {
      clearTimeout(timeout);
      this.timeouts.delete(id);
    }
  }

  resumeAutoDismiss(id: string): void {
    if (!this.timeouts.has(id)) {
      const timeout = setTimeout(() => this.dismiss(id), this.AUTO_DISMISS_MS);
      this.timeouts.set(id, timeout);
    }
  }
}
